import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { IconTrash, IconEdit } from '../components/Icons';

const Admin = () => {
  const { logout } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [formData, setFormData] = useState({ name: '', type: 'sender', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('contacts');

  // Contact import/export state. Operators use this to copy
  // payers/recipients/concepts between projects without retyping —
  // Export downloads a JSON file, Import shows a file picker +
  // preview modal that dedupes against the target project's existing
  // contacts before confirming. UI-only state lives here; the
  // backend hits /api/contacts/export + /api/contacts/import.
  const [contactExportType, setContactExportType] = useState('');  // '' = all
  const [exportingContacts, setExportingContacts] = useState(false);
  const [exportMsg, setExportMsg] = useState('');

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // {contacts:[...], count, fileName}
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // {created, skipped, errors}
  const importFileRef = useRef(null);
  
  // Backup state
  const [backups, setBackups] = useState([]);
  const [lastBackup, setLastBackup] = useState(null);
  const [autoBackup, setAutoBackup] = useState(0);
  const [backupDays, setBackupDays] = useState(7);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState('');
  const [backupError, setBackupError] = useState('');

  // Delivery / Public URL state (Phase 1)
  const [delivery, setDelivery] = useState({
    publicUrl: '',
    companyName: 'ReceiptWise',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',           // masked as "" unless the user types a new one
    smtpPasswordSet: false,     // true when the saved DB value is non-empty
    smtpFrom: '',
    smtpFromName: 'ReceiptWise',
    smtpUseTls: true,
    waDefaultCc: '+52',
  });
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliverySuccess, setDeliverySuccess] = useState('');
  const [deliveryError, setDeliveryError] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('selectedProjectId');
    if (saved) setSelectedProjectId(parseInt(saved));
    // Theme is now managed globally in <Header>; this page just reads it.
  }, []);

  // fetchContacts and fetchBackups close over the current state via setState
  // updaters only; an exhaustive-deps lint rule would force us to memoize
  // them, which adds noise without changing behavior. The current effect
  // is correct: re-run when the project or active tab changes.
  useEffect(() => {
    if (selectedProjectId) fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  useEffect(() => {
    if (activeTab === 'backup') fetchBackups();
    if (activeTab === 'delivery') fetchDelivery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchDelivery = async () => {
    setDeliveryLoading(true);
    setDeliveryError('');
    try {
      const data = await api.getSettings();
      const s = data?.settings || {};
      const dc = s.deliveryConfig || {};
      setDelivery({
        publicUrl: s.publicUrl || '',
        companyName: s.companyName || 'ReceiptWise',
        smtpHost: dc.smtpHost || '',
        smtpPort: dc.smtpPort || 587,
        smtpUser: dc.smtpUser || '',
        smtpPassword: '',                // never populated from GET — user must type to change
        smtpPasswordSet: !!dc.smtpPasswordSet,
        smtpFrom: dc.smtpFrom || '',
        smtpFromName: dc.smtpFromName || 'ReceiptWise',
        smtpUseTls: dc.smtpUseTls !== false,
        waDefaultCc: dc.waDefaultCc || '+52',
      });
    } catch (err) {
      setDeliveryError(err.message);
    } finally {
      setDeliveryLoading(false);
    }
  };

  const saveDelivery = async (e) => {
    e && e.preventDefault();
    setDeliveryError('');
    setDeliverySuccess('');
    try {
      // Build the Settings payload — preserves the existing
      // BackupDays / NextQuoteID by reading them from the API first.
      const current = await api.getSettings();
      const s = current?.settings || {};
      const merged = {
        BackupDays: s.backupDays || 0,
        NextQuoteID: s.nextQuoteID || 0,
        PublicURL: delivery.publicUrl,
        CompanyName: delivery.companyName,
        DeliveryConfig: {
          SMTPHost: delivery.smtpHost,
          SMTPPort: Number(delivery.smtpPort) || 587,
          SMTPUser: delivery.smtpUser,
          SMTPPassword: delivery.smtpPassword,  // "" preserves existing; non-empty overwrites
          SMTPFrom: delivery.smtpFrom,
          SMTPFromName: delivery.smtpFromName,
          SMTPUseTLS: !!delivery.smtpUseTls,
          WADefaultCC: delivery.waDefaultCc || '+52',
        },
      };
      await api.updateSettings(merged);
      setDeliverySuccess('✓ Configuración guardada');
      setDelivery(d => ({ ...d, smtpPassword: '', smtpPasswordSet: delivery.smtpPassword !== '' || d.smtpPasswordSet }));
      setTimeout(() => setDeliverySuccess(''), 3000);
    } catch (err) {
      setDeliveryError(err.message);
    }
  };

  const sendTestEmail = async () => {
    setTestEmailSending(true);
    setTestEmailResult('');
    setDeliveryError('');
    try {
      await saveDelivery();                  // persist before testing
      const r = await api.sendTestEmail();
      setTestEmailResult('✓ ' + (r.message || 'Email de prueba enviado. Revisa tu bandeja.'));
    } catch (err) {
      setTestEmailResult('');
      setDeliveryError(err.message);
    } finally {
      setTestEmailSending(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const data = await api.getContacts(selectedProjectId);
      setContacts(data);
    } catch (err) { console.error('Failed:', err); }
    finally { setLoading(false); }
  };

  const fetchBackups = async () => {
    setLoadingBackups(true);
    setBackupError('');
    try {
      const data = await api.getSettings();
      setBackups(data.backups || []);
      setLastBackup(data.lastBackup);
      setAutoBackup(data.settings?.backupDays || 0);
      setBackupDays(data.settings?.backupDays || 7);
    } catch (err) { setBackupError(err.message); }
    finally { setLoadingBackups(false); }
  };

  // Export the current project's contacts as a JSON file. The
  // response carries a Content-Disposition: attachment header with
  // a server-generated filename (e.g.
  // contacts-tienda_liconsa-sender-2026-07-19.json) so multiple
  // exports from different projects don't clobber each other on
  // disk. We synthesise the download via Blob in the browser.
  const handleExportContacts = async () => {
    if (exportingContacts) return;
    if (!selectedProjectId) {
      setExportMsg('Selecciona un proyecto primero.');
      return;
    }
    setExportingContacts(true);
    setExportMsg('');
    try {
      const { data, filename } = await api.exportContacts(
        selectedProjectId, contactExportType);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const n = data.count ?? data.contacts?.length ?? 0;
      setExportMsg(`Exportados ${n} contacto(s) a ${filename}.`);
    } catch (err) {
      setExportMsg(`Error: ${err.message}`);
    } finally {
      setExportingContacts(false);
    }
  };

  // Import: read the file, parse JSON, show a preview before
  // POSTing. The backend dedups and creates, returning a summary
  // {created, skipped, errors} that we render inline.
  const handleImportFile = (e) => {
    setImportError('');
    setImportResult(null);
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const contacts = Array.isArray(parsed) ? parsed : (parsed.contacts || []);
        if (!Array.isArray(contacts) || contacts.length === 0) {
          setImportError('El archivo no contiene contactos (espera un objeto con campo "contacts" o un array).');
          return;
        }
        // Coerce structure: backend accepts {name, type, phone, email}.
        const trimmed = contacts.map((c) => ({
          name:  String(c.name || '').trim(),
          type:  String(c.type || '').trim(),
          phone: c.phone ? String(c.phone).trim() : '',
          email: c.email ? String(c.email).trim() : '',
          country: c.country ? String(c.country).trim() : '',
        })).filter((c) => c.name);
        if (trimmed.length === 0) {
          setImportError('El archivo no contiene contactos con nombre válido.');
          return;
        }
        setImportFile(f);
        setImportPreview({ contacts: trimmed, count: trimmed.length, fileName: f.name });
      } catch (err) {
        setImportError(`JSON inválido: ${err.message}`);
      }
    };
    reader.readAsText(f);
  };

  const handleImportConfirm = async () => {
    if (!importPreview || !selectedProjectId) return;
    setImporting(true);
    setImportError('');
    try {
      const result = await api.importContacts(selectedProjectId, importPreview.contacts);
      setImportResult(result);
      // Refresh contacts list so the new ones show up in the tabs.
      fetchContacts();
      // Clear the file input so the same file can be re-picked later.
      if (importFileRef.current) importFileRef.current.value = '';
      setImportPreview(null);
      setImportFile(null);
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    if (importing) return;
    setShowImportModal(false);
    setImportFile(null);
    setImportPreview(null);
    setImportError('');
    setImportResult(null);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const saveBackupDays = async (days) => {
    setBackupError('');
    try {
      await api.updateSettings({ backupDays: parseInt(days) });
      setBackupDays(parseInt(days));
      setAutoBackup(parseInt(days));
      alert('Settings saved!');
    } catch (err) { setBackupError(err.message); }
  };

  const createBackup = async () => {
    setBackupError('');
    setBackupSuccess('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/backups', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBackupSuccess('Backup created!');
        fetchBackups();
        setTimeout(() => setBackupSuccess(''), 3000);
      } else {
        const data = await response.json();
        setBackupError(data.error || 'Failed');
      }
    } catch (err) { setBackupError(err.message); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Phase 1: send {name, type, phone?, email?} so the new hash
      // format and WhatsApp delivery have something to bind to.
      const payload = {
        name: formData.name,
        type: formData.type,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
      };
      await api.createContact(payload, selectedProjectId);
      setShowModal(false);
      setFormData({ name: '', type: 'sender', phone: '', email: '' });
      fetchContacts();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await api.deleteContact(id); fetchContacts(); }
    catch (err) { alert(err.message); }
  };

  // Edit a contact's name/phone/email. The contact's type and project
  // membership are set at create time and not editable here — delete +
  // re-add if you need to change those.
  const [editingContact, setEditingContact] = useState(null); // {id, name, phone, email}
  const [editContactSaving, setEditContactSaving] = useState(false);
  const [editContactError, setEditContactError] = useState('');
  const openContactEdit = (contact) => {
    setEditingContact({
      id: contact.id,
      name: contact.name || '',
      phone: contact.phone || '',
      email: contact.email || '',
    });
    setEditContactError('');
  };
  const closeContactEdit = () => {
    if (editContactSaving) return;
    setEditingContact(null);
    setEditContactError('');
  };
  const saveContactEdit = async () => {
    if (!editingContact) return;
    setEditContactSaving(true);
    setEditContactError('');
    try {
      await api.updateContact(editingContact.id, {
        name: editingContact.name,
        phone: editingContact.phone,
        email: editingContact.email,
      });
      setEditingContact(null);
      fetchContacts();
    } catch (err) {
      setEditContactError(err.message || 'Failed to update contact');
    } finally {
      setEditContactSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordData.newPassword !== passwordData.confirmPassword) { setPasswordError('Passwords do not match'); return; }
    if (passwordData.newPassword.length < 6) { setPasswordError('Minimum 6 characters'); return; }
    try {
      await api.changePassword(passwordData.oldPassword, passwordData.newPassword);
      setPasswordSuccess('Password changed!');
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowPasswordModal(false); setPasswordSuccess(''); }, 2000);
    } catch (err) { setPasswordError(err.message); }
  };

  const senders = (contacts || []).filter(c => c.type === 'sender');
  const recipients = (contacts || []).filter(c => c.type === 'recipient');
  const concepts = (contacts || []).filter(c => c.type === 'concept');

  const getTypeLabel = (type) => ({ sender: 'Payer', recipient: 'Recipient', concept: 'Concept' }[type] || type);

  const ContactList = ({ title, items, type }) => (
    <div className="admin-section">
      <div className="section-header">
        <h2>{title}</h2>
        <button onClick={() => { setFormData({ name: '', type, phone: '', email: '' }); setShowModal(true); }} className="btn btn-primary">+ Add</button>
      </div>
      {loading ? <div className="loading"><div className="spinner"></div></div> : items.length === 0 ? (
        <div className="empty-state"><p>No {title.toLowerCase()} yet</p></div>
      ) : (
        <div className="contacts-list">
          {items.map(contact => (
            <div key={contact.id} className="contact-item">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.15rem' }}>
                <span>{contact.name}</span>
                {(contact.phone || contact.email) && (
                  <small style={{ color: 'var(--text-muted)' }}>
                    {contact.phone && <>📱 {contact.phone}</>}
                    {contact.phone && contact.email && ' · '}
                    {contact.email && <>✉️ {contact.email}</>}
                  </small>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  onClick={() => openContactEdit(contact)}
                  className="btn-delete"
                  title="Edit name / phone / email"
                >
                  <IconEdit size={14} />
                </button>
                <button onClick={() => handleDelete(contact.id)} className="btn-delete"><IconTrash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>⚙️ Config</h1>
        <div className="header-actions">
          <button onClick={() => window.location.href = '/'} className="btn btn-secondary">← Dashboard</button>
          <button onClick={() => window.location.href = '/users'} className="btn btn-outline">👥 Users</button>
          <button onClick={() => window.location.href = '/projects'} className="btn btn-outline">📁 Projects</button>
          <button onClick={() => setShowPasswordModal(true)} className="btn btn-outline">🔐</button>
          <button onClick={logout} className="btn btn-outline">Logout</button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'contacts' ? 'active' : ''}`} onClick={() => setActiveTab('contacts')}>📝 Contacts</button>
        <button className={`tab ${activeTab === 'delivery' ? 'active' : ''}`} onClick={() => setActiveTab('delivery')}>📨 Delivery</button>
        <button className={`tab ${activeTab === 'backup' ? 'active' : ''}`} onClick={() => setActiveTab('backup')}>💾 Backups</button>
      </div>
      
      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <>
          <div className="admin-stats">
            <div className="stat-card"><h3>Payers</h3><div className="value">{senders.length}</div></div>
            <div className="stat-card"><h3>Recipients</h3><div className="value">{recipients.length}</div></div>
            <div className="stat-card"><h3>Concepts</h3><div className="value">{concepts.length}</div></div>
          </div>

          {/* Contact import/export toolbar. Operators use this to
              copy payers/recipients/concepts between projects without
              retyping. Export downloads a JSON file; Import opens a
              modal with a file picker + preview that dedupes against
              the target project's existing contacts server-side. */}
          <div className="contact-import-export-toolbar">
            <div className="toolbar-row">
              <label className="toolbar-label" htmlFor="contact-export-type">Filter export by type:</label>
              <select id="contact-export-type" className="form-select"
                value={contactExportType} onChange={(e) => setContactExportType(e.target.value)}
                title="Choose a contact type to export only those; choose 'All' to export senders + recipients + concepts.">
                <option value="">All types</option>
                <option value="sender">Senders (payers)</option>
                <option value="recipient">Recipients</option>
                <option value="concept">Concepts</option>
              </select>
              <button type="button" className="btn btn-outline" onClick={handleExportContacts}
                disabled={exportingContacts || !selectedProjectId}
                title="Download the contacts as a JSON file. The file can be imported into another project.">
                {exportingContacts ? 'Exportando…' : '📥 Export JSON'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { setShowImportModal(true); }}
                disabled={!selectedProjectId}
                title="Import a contacts JSON file from another project. Existing contacts with the same name + type are skipped.">
                📤 Import JSON
              </button>
            </div>
            {exportMsg && (
              <div className={`toolbar-status ${exportMsg.startsWith('Error') ? 'error' : 'ok'}`}>
                {exportMsg}
              </div>
            )}
            {!selectedProjectId && (
              <div className="toolbar-status muted">Select a project (top right) to enable import/export.</div>
            )}
          </div>

          <ContactList title="📤 Payers" items={senders} type="sender" />
          <ContactList title="📥 Recipients" items={recipients} type="recipient" />
          <ContactList title="📝 Concepts" items={concepts} type="concept" />
        </>
      )}
      
      {/* Delivery Tab (Phase 1) — public URL + SMTP creds + WhatsApp default */}
      {activeTab === 'delivery' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>📨 Delivery & Public URL</h2>
          </div>
          <p style={{ color: 'var(--text-muted)', maxWidth: '720px', marginBottom: '1rem' }}>
            Configura la URL p&uacute;blica de tu despliegue (se usa en los QR de los PDFs,
            en los enlaces de verificaci&oacute;n y en los mensajes de WhatsApp) y las
            credenciales SMTP para enviar recibos por email.
          </p>

          {deliveryLoading ? <div className="loading"><div className="spinner"></div></div> : (
            <form onSubmit={saveDelivery} style={{ display: 'grid', gap: '1.25rem', maxWidth: '720px' }}>
              {/* Public URL */}
              <fieldset style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, padding: '1rem' }}>
                <legend style={{ padding: '0 .5rem', fontWeight: 600 }}>URL p&uacute;blica</legend>
                <div className="form-group">
                  <label>URL p&uacute;blica del despliegue</label>
                  <input
                    type="url"
                    value={delivery.publicUrl}
                    onChange={e => setDelivery({ ...delivery, publicUrl: e.target.value })}
                    placeholder="https://recibos.techno-world.net"
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Los QR, los enlaces de verificaci&oacute;n y los mensajes de WhatsApp usar&aacute;n esta URL.
                  </small>
                </div>
                <div className="form-group">
                  <label>Nombre de la empresa</label>
                  <input
                    type="text"
                    value={delivery.companyName}
                    onChange={e => setDelivery({ ...delivery, companyName: e.target.value })}
                    placeholder="ReceiptWise"
                  />
                </div>
              </fieldset>

              {/* SMTP */}
              <fieldset style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, padding: '1rem' }}>
                <legend style={{ padding: '0 .5rem', fontWeight: 600 }}>SMTP (Email)</legend>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '.75rem' }}>
                  <div className="form-group">
                    <label>Servidor SMTP</label>
                    <input
                      type="text"
                      value={delivery.smtpHost}
                      onChange={e => setDelivery({ ...delivery, smtpHost: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Puerto</label>
                    <input
                      type="number"
                      value={delivery.smtpPort}
                      onChange={e => setDelivery({ ...delivery, smtpPort: e.target.value })}
                      placeholder="587"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Usuario</label>
                  <input
                    type="text"
                    value={delivery.smtpUser}
                    onChange={e => setDelivery({ ...delivery, smtpUser: e.target.value })}
                    placeholder="recibos@techno-world.net"
                  />
                </div>
                <div className="form-group">
                  <label>Contrase&ntilde;a {delivery.smtpPasswordSet && !delivery.smtpPassword && <small style={{ color: 'var(--text-muted)' }}>(guardada &mdash; deja vac&iacute;o para conservar)</small>}</label>
                  <input
                    type="password"
                    value={delivery.smtpPassword}
                    onChange={e => setDelivery({ ...delivery, smtpPassword: e.target.value })}
                    placeholder={delivery.smtpPasswordSet ? '••••••' : 'Contraseña o app-password'}
                    autoComplete="new-password"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                  <div className="form-group">
                    <label>From (direcci&oacute;n)</label>
                    <input
                      type="email"
                      value={delivery.smtpFrom}
                      onChange={e => setDelivery({ ...delivery, smtpFrom: e.target.value })}
                      placeholder="recibos@techno-world.net"
                    />
                  </div>
                  <div className="form-group">
                    <label>From (nombre)</label>
                    <input
                      type="text"
                      value={delivery.smtpFromName}
                      onChange={e => setDelivery({ ...delivery, smtpFromName: e.target.value })}
                      placeholder="ReceiptWise"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!delivery.smtpUseTls}
                      onChange={e => setDelivery({ ...delivery, smtpUseTls: e.target.checked })}
                    />
                    Usar STARTTLS (recomendado)
                  </label>
                </div>
              </fieldset>

              {/* WhatsApp */}
              <fieldset style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, padding: '1rem' }}>
                <legend style={{ padding: '0 .5rem', fontWeight: 600 }}>WhatsApp</legend>
                <div className="form-group">
                  <label>C&oacute;digo de pa&iacute;s por defecto</label>
                  <input
                    type="text"
                    value={delivery.waDefaultCc}
                    onChange={e => setDelivery({ ...delivery, waDefaultCc: e.target.value })}
                    placeholder="+52"
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Se antepone a los tel&eacute;fonos que no tengan el s&iacute;mbolo &laquo;+&raquo;.
                  </small>
                </div>
              </fieldset>

              {deliveryError && <div className="error-message">{deliveryError}</div>}
              {deliverySuccess && <div className="success-message">{deliverySuccess}</div>}
              {testEmailResult && <div className="success-message">{testEmailResult}</div>}

              <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="submit" className="btn btn-primary" disabled={deliveryLoading}>
                  💾 Guardar configuraci&oacute;n
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={testEmailSending || !delivery.smtpHost}
                  onClick={sendTestEmail}
                  title={!delivery.smtpHost ? 'Configura el servidor SMTP primero' : ''}
                >
                  {testEmailSending ? 'Enviando…' : '✉️ Enviar email de prueba'}
                </button>
                <small style={{ color: 'var(--text-muted)' }}>
                  El email de prueba llega al usuario autenticado.
                </small>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Backups Tab */}
      {activeTab === 'backup' && (
        <div className="admin-section">
          <div className="section-header">
            <h2>💾 Database Backups</h2>
          </div>
          
          <div className="backup-info">
            <p><strong>Auto-backup:</strong> {autoBackup > 0 ? `Every ${autoBackup} days` : 'Disabled'}</p>
            <p><strong>Last backup:</strong> {lastBackup ? new Date(lastBackup).toLocaleString() : 'Never'}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label>Interval:</label>
              <select value={backupDays} onChange={(e) => saveBackupDays(e.target.value)} style={{ padding: '0.5rem' }}>
                <option value="0">Disabled</option>
                <option value="1">Daily</option>
                <option value="7">Weekly</option>
                <option value="14">Every 2 weeks</option>
                <option value="30">Monthly</option>
              </select>
            </div>
          </div>
          
          <div style={{ margin: '1.5rem 0', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={createBackup} disabled={loadingBackups} className="btn btn-primary">
              {loadingBackups ? 'Creating...' : 'Create Backup Now'}
            </button>
            {backupSuccess && <span className="success-message">{backupSuccess}</span>}
            {backupError && <span className="error-message">{backupError}</span>}
          </div>
          
          <h3 style={{ marginTop: '1.5rem' }}>Available Backups</h3>
          {loadingBackups ? <div className="loading"><div className="spinner"></div></div> : backups.length === 0 ? (
            <p className="empty-state">No backups yet</p>
          ) : (
            <div className="contacts-list">
              {backups.map(backup => (
                <div key={backup} className="contact-item">
                  <span>📄 {backup}</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="btn btn-sm btn-danger" 
                      onClick={async () => {
                        if (!window.confirm('Delete this backup?')) return;
                        try {
                          await api.deleteBackup(backup);
                          fetchBackups();
                          alert('Backup deleted!');
                        } catch (err) {
                          alert(err.message);
                        }
                      }}
                    >Delete</button>
                    <button 
                      className="btn btn-sm" 
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`/api/backups/${backup}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (!response.ok) throw new Error('Download failed');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = backup;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (err) {
                          alert(err.message);
                        }
                      }}
                    >Download</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Contact Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add {getTypeLabel(formData.type)}</h2>
            <form onSubmit={handleSubmit}>
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Enter name" required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select name="type" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}>
                  <option value="sender">Sender</option><option value="recipient">Recipient</option><option value="concept">Concept</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tel&eacute;fono (opcional)</label>
                <input type="tel" name="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+52 55 1234 5678" />
                <small style={{ color: 'var(--text-muted)' }}>E.164 con prefijo &laquo;+&raquo;. Se usa para WhatsApp y queda ligado al recibo.</small>
              </div>
              <div className="form-group">
                <label>Email (opcional)</label>
                <input type="email" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="cliente@empresa.com" />
                <small style={{ color: 'var(--text-muted)' }}>Se usa para enviar el recibo por correo.</small>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" disabled={submitting} className="btn btn-primary">{submitting ? 'Adding...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>🔐 Change Password</h2>
            <form onSubmit={handlePasswordChange}>
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
              <div className="form-group"><label>Current</label><input type="password" value={passwordData.oldPassword} onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})} required /></div>
              <div className="form-group"><label>New</label><input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} required /></div>
              <div className="form-group"><label>Confirm</label><input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} required /></div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowPasswordModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Change</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit contact modal: name/phone/email only. Type + project are
          set at creation; delete + re-add if you need to change those. */}
      {editingContact && (
        <div className="modal-overlay" onClick={closeContactEdit}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2><IconEdit size={18} /> Edit contact</h2>
              <button className="modal-close" onClick={closeContactEdit}>×</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveContactEdit(); }}>
              <div className="modal-body">
                {editContactError && <div className="alert alert-error">{editContactError}</div>}
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editingContact.name}
                    onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                    disabled={editContactSaving}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone (with country code)</label>
                  <input
                    type="tel"
                    value={editingContact.phone}
                    onChange={(e) => setEditingContact({ ...editingContact, phone: e.target.value })}
                    placeholder="+525512345678"
                    disabled={editContactSaving}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editingContact.email}
                    onChange={(e) => setEditingContact({ ...editingContact, email: e.target.value })}
                    placeholder="cobros@acme.mx"
                    disabled={editContactSaving}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeContactEdit} disabled={editContactSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editContactSaving}>
                  {editContactSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Import Contacts Modal: file picker → preview → confirm.
           The handler returns a {created, skipped, errors} summary that
           we display in the same modal so the operator sees the
           outcome without leaving the Admin → Contacts page. */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>📤 Import Contacts</h2>
              <button className="modal-close" onClick={closeImportModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <p className="modal-intro">
                Pick the JSON you exported from another project's Contacts tab.
                Contacts with the same name (case-insensitive) + type in the current
                project will be <strong>skipped</strong>; the rest will be created.
              </p>
              <div className="form-group">
                <label htmlFor="contact-import-file">JSON file</label>
                <input
                  id="contact-import-file"
                  ref={importFileRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={handleImportFile}
                  disabled={importing}
                />
              </div>
              {importPreview && (
                <div className="import-preview">
                  <h4>Preview — {importPreview.count} contact(s) parsed from {importPreview.fileName}</h4>
                  <div className="import-preview-table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Name</th><th>Type</th><th>Phone</th><th>Email</th></tr></thead>
                      <tbody>
                        {importPreview.contacts.slice(0, 50).map((c, i) => (
                          <tr key={i}>
                            <td>{c.name}</td>
                            <td>{c.type}</td>
                            <td>{c.phone || '—'}</td>
                            <td>{c.email || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importPreview.contacts.length > 50 && (
                      <div className="import-preview-more">… and {importPreview.contacts.length - 50} more (only the first 50 shown here; all will be imported).</div>
                    )}
                  </div>
                </div>
              )}
              {importError && (
                <div className="toolbar-status error">{importError}</div>
              )}
              {importResult && (
                <div className="import-result">
                  <div className="import-result-stat ok">✓ Creados: {importResult.created}</div>
                  <div className="import-result-stat muted">→ Saltados (duplicados): {importResult.skipped}</div>
                  {importResult.requested != null && (
                    <div className="import-result-stat muted">Total solicitados: {importResult.requested}</div>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="import-errors">
                      <h4>Errores ({importResult.errors.length})</h4>
                      <ul>
                        {importResult.errors.map((e, i) => (<li key={i}>{e}</li>))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeImportModal} disabled={importing}>Cancel</button>
              {importPreview && !importResult && (
                <button type="button" className="btn btn-primary" onClick={handleImportConfirm} disabled={importing}>
                  {importing ? 'Importando…' : `📥 Importar ${importPreview.count} contacto(s)`}
                </button>
              )}
              {importResult && (
                <button type="button" className="btn btn-primary" onClick={closeImportModal}>Cerrar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
